import { HttpParams } from '@angular/common/http'
import { Component, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { PropertyGroupDescription, SortDescription } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem, ModalSize, SchedulingJob, SchedulingTask } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { SchedulingTaskComponent } from './scheduling-task.component'
import { SchedulingTransportsComponent } from './transports/transports.component'

@Component({
    templateUrl: './scheduling.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridColumnGroupComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        FaIconComponent,
        ActionToolbarButtonsComponent,
        SchedulingTransportsComponent,
    ],
})
export class SchedulingComponent extends BaseComponent {
    faMap = faLocationDot
    activeTab = 0
    grid: Grid<any>
    jobPhaseGrid: Grid<any>
    taskGrid: Grid<any>
    phaseGrid: Grid<any>
    priorityMap!: DataMap
    jobPriorityMap!: DataMap
    priorityCodes = [
        { value: 100, display: 'PC' },
        { value: 101, display: 'PL' },
        { value: 102, display: 'OH' },
    ]
    sortMode: 'default' | 'lettersFirst' = 'default'
    sortModeOptions = [
        { value: 'default', display: 'Priority (1-20, then PC/PL/OH)' },
        { value: 'lettersFirst', display: 'Letters first (PC/PL/OH, then 1-20)' },
    ]
    initializingMultiSelect = false
    checkedItems = [] as any[]
    jobs = signal<SchedulingJob[]>([])
    jobMap!: DataMap
    jobOptions: ListItem<string>[] = []
    selectedJob!: string
    phaseData: any[] = []
    addressString = ''
    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsSchedulingEdit])
    }

    constructor() {
        super()

        this.grid = new Grid<any>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.jobName])
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)

        this.jobPhaseGrid = new Grid<any>(this.services, 'id', this).filterBy(item => [item.phase, item.costTypes, item.description])

        this.taskGrid = new Grid<SchedulingTask>(this.services, 'id', this)
            .setupModalEditing(
                this.services.data.schedulingTasks,
                'Task',
                SchedulingTaskComponent,
                () => new SchedulingTask(),
                this,
                ModalSize.large,
            )
            .wrapColumnHeaders()
            .filterBy(item => [item.job, item.name, item.crewAssignment, item.materialsNeeded])
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)

        this.phaseGrid = new Grid<SchedulingTask>(this.services, 'id', this).wrapColumnHeaders().autoGenerateColumns(true)

        const priorities = [] as any[]
        for (let i = 1; i <= 20; i++) {
            priorities.push(new ListItem(i, i.toString()))
        }
        this.priorityMap = new DataMap(priorities, 'value', 'display')

        const jobPriorities = priorities.concat(this.priorityCodes.map(x => new ListItem(x.value, x.display)))
        this.jobPriorityMap = new DataMap(jobPriorities, 'value', 'display')

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(true, true, this.services.data.schedulingJobs.queryAll(), this.services.data.schedulingTasks.queryAll())
                .subscribe({
                    next: () => {
                        this.initialLoadingComplete()
                        for (const item of this.services.data.schedulingJobs.items) {
                            item.departmentList = []
                            const departments = item.departments.split(',')
                            for (const department of departments) {
                                if (department === '') {
                                    continue
                                }
                                item.departmentList.push({
                                    id: +department,
                                    value: this.departmentOptions.find(x => x.id === +department)?.value,
                                    $checked: true,
                                })
                            }
                        }
                        this.grid.items = this.services.data.schedulingJobs.items
                        this.grid.view.groupDescriptions.clear()
                        this.grid.view.groupDescriptions.push(new PropertyGroupDescription('divisionName'))
                        this.grid.flexGrid.collapseGroupsToLevel(0)
                        this.applyJobPrioritySort()
                        this.taskGrid.items = this.services.data.schedulingTasks.items

                        this.jobs.set(this.services.data.schedulingJobs.items.slice())
                        this.jobMap = new DataMap(this.services.data.schedulingJobs.items, 'job', 'jobName')
                        this.jobOptions = this.services.data.schedulingJobs.items
                            .map(x => new ListItem(x.job, x.jobName))
                            .orderBy(x => x.value)
                    },
                }),
        )
    }

    applyJobPrioritySort() {
        for (const item of this.grid.items as SchedulingJob[]) {
            const priority = item.priority
            if (this.sortMode === 'lettersFirst') {
                item.prioritySort = priority >= 100 ? priority - 100 : priority + 100
            } else {
                item.prioritySort = priority
            }
        }
        this.grid.view.sortDescriptions.clear()
        this.grid.view.sortDescriptions.push(new SortDescription('prioritySort', true))
    }

    onSortModeChanged() {
        this.applyJobPrioritySort()
    }

    mainGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]

            cell.style.height = '42px'
            cell.classList.remove('yellow-cell')

            if (
                panel.grid['controller'] &&
                panel.grid['controller'].canEdit &&
                col.binding !== 'jobName' &&
                col.binding !== 'start' &&
                col.binding !== 'end'
            ) {
                cell.classList.add('yellow-cell')
            }

            if (col.binding === 'departmentList') {
                console.log(cell)
            }
        }
    }

    jobPhaseGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const rowItem = panel.grid.rows[r].dataItem
            cell.classList.remove('green-cell')

            if (col.binding === 'isActive' && rowItem.isActive) {
                cell.classList.add('green-cell')
            }
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        if (flexGrid.hostElement.id === 'gridTask') {
            this.updateTasks([flexGrid.rows[args.row].dataItem])
        } else if (flexGrid.hostElement.id === 'mainGrid') {
            this.updateJobs([flexGrid.rows[args.row].dataItem])
        }
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        flexGrid['controller'].itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => flexGrid['controller'].itemIds.indexOf(x.id) > -1)
        if (flexGrid.hostElement.id === 'gridTask') {
            this.updateTasks(itemsToSave)
        } else if (flexGrid.hostElement.id === 'mainGrid') {
            this.updateJobs(itemsToSave)
        }
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }

    updateJobs(items: SchedulingJob[]) {
        for (const item of items) {
            item.departments = item.departmentList.map(x => x.id).join(',')
        }
        this.services.data.put('api/SchedulingJob/BulkUpdate', items).subscribe({
            next: () => {
                this.services.data.schedulingJobs.updateSort()
                this.grid.refresh()
                this.applyJobPrioritySort()
            },
            error: () => {
                this.services.notification.error('The schedule changes were not successfully saved')
            },
        })
    }

    updateTasks(items: SchedulingTask[]) {
        this.services.data.put('api/SchedulingTask/BulkUpdate', items).subscribe({
            next: () => {
                this.services.data.schedulingTasks.updateSort()
                this.taskGrid.refresh()
            },
            error: () => {
                this.services.notification.error('The schedule changes were not successfully saved')
            },
        })
    }

    getCellValue(cell) {
        return cell.item.departmentList.map(x => x.value).join(', ')
    }

    checkChanged(multiselect, cell) {
        if (this.initializingMultiSelect) {
            setTimeout(() => {
                multiselect.checkedItems = this.checkedItems
                this.initializingMultiSelect = false
            }, 10)
        }
        cell.value = cell.item.departmentList
    }

    itemsSourceChanged(multiselect, cell) {
        const checkedItems = [] as any[]
        for (const item of multiselect.collectionView.items) {
            for (const list of cell.item.departmentList) {
                if (item.value === list.value) {
                    item.$checked = true
                    checkedItems.push(item)
                }
            }
        }
        this.initializingMultiSelect = true
        this.checkedItems = checkedItems

        multiselect.checkedItems = checkedItems
    }

    tabChanged(newActiveTab) {
        if (newActiveTab === -3) {
            if (this && this.grid && this.grid.flexGrid && this.grid.flexGrid.collectionView) {
                this.taskGrid.flexGrid.collectionView.filter = x => x
            }
        }

        // If the user picked a department tab, we need to filter the items to just those department tasks.
        if (newActiveTab > 0) {
            if (this && this.grid && this.grid.flexGrid && this.grid.flexGrid.collectionView) {
                this.taskGrid.flexGrid.collectionView.filter = x => x.departmentId === newActiveTab
            }
        }
        if (newActiveTab === -1) {
            if (this.phaseData.length === 0) {
                this.cancelableRequest(
                    'phase',
                    true,
                    this.services.data.load(true, true, this.services.data.get('api/SchedulingJob/GetPhaseData')).subscribe({
                        next: successResult => {
                            this.phaseData = successResult[0]
                            this.phaseGrid.items = this.phaseData
                        },
                    }),
                )
            }
        }
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    jobChanged() {
        const params = new HttpParams().set('job', this.selectedJob)
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.get('api/SchedulingJob/GetJobPhases', params)).subscribe({
                next: successResult => {
                    this.jobPhaseGrid.items = successResult[0]
                },
            }),
        )

        this.addressString = ''
        const match = this.grid.items.find(x => x.job == this.selectedJob) as any
        if (match && match.mailAddress) {
            this.addressString = match.mailAddress + ', ' + match.shipCity + ', ' + match.shipState
        } else {
            this.addressString = 'No Address Found'
        }
    }

    navigate() {
        const match = this.grid.items.find(x => x.job == this.selectedJob) as any
        if (!match || !match.mailAddress) {
            this.services.notification.info('No Address was found for the selected job')
            return
        }

        window.open('http://maps.google.com/?q=' + this.addressString, '_blank')
    }

    departmentOptions: { id: number; value: string }[] = [
        { id: 15, value: 'Concrete' },
        { id: 10, value: 'Pipe' },
        { id: 11, value: 'Earth Work' },
        { id: 12, value: 'Finishes' },
        { id: 55, value: 'Mechanical' },
        { id: 40, value: 'Drivers' },
        { id: 45, value: 'Survey' },
        { id: 20, value: 'Fabrication' },
        { id: 1000, value: 'Misc' },
        { id: 1001, value: 'Vac Truck' },
        { id: 1002, value: 'Boom Truck' },
    ]

    departmentMap = new DataMap(this.departmentOptions, 'id', 'value')
}
